import React, { useId, useState } from "react";
import {
  Box,
  Button,
  ExpandableSection,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import {
  OmsExportPreset,
  OmsPresetScope,
  OmsReport,
} from "../../lusca-search-api/generated-src";
import { OmsConfiguration } from "./omsPresetEditing";
import presetChanges, { PresetChangeValue } from "./omsPresetChanges";
import { comparisonLabel } from "./omsReportFormatting";
import { submittedCondition } from "./omsFilterEditing";

function PresetChangeCell({
  value,
  report,
}: {
  value: PresetChangeValue;
  report: OmsReport;
}) {
  const [expanded, setExpanded] = useState(false);
  const detailsId = useId();
  const { filter } = value;
  const text = filter
    ? comparisonLabel(filter.operator, report.columnTypes?.[filter.column])
    : value.text;
  const values = filter
    ? filter.values || (filter.value !== undefined ? [filter.value] : [])
    : value.values || [];
  const labels = values.map((item) => (item === "" ? '"" (blank)' : item));
  let unit = "column";
  if (filter) unit = "value";
  else if (value.kind === "filters") unit = "filter";
  if (labels.length > 3 || labels.join(", ").length > 160)
    return (
      <SpaceBetween size="xs">
        {text && <span>{text}</span>}
        <Button
          ariaExpanded={expanded}
          ariaControls={detailsId}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? "Hide" : "Show"} {labels.length}{" "}
          {labels.length === 1 ? unit : `${unit}s`}
        </Button>
        <div id={detailsId} hidden={!expanded}>
          {expanded && (
            <ol className="oms-report-download__applied-filters">
              {labels.map((item, index) => (
                // Duplicate values are possible in an ordered filter-field list.
                // eslint-disable-next-line react/no-array-index-key
                <li key={index}>{item}</li>
              ))}
            </ol>
          )}
        </div>
      </SpaceBetween>
    );
  return (
    <span>
      {text}
      {text && labels.length > 0 && " "}
      {labels.join(", ")}
    </span>
  );
}

export function OMSPresetContents({
  draft,
  scope,
  report,
}: {
  draft: OmsConfiguration;
  scope: OmsPresetScope;
  report: OmsReport;
}) {
  const [expanded, setExpanded] = useState(true);
  const allColumns = draft.columns.strategy === "all";
  const names = allColumns ? report.columns : draft.columns.names;
  const selectedLabel = names.length
    ? "Selected columns (CSV order)"
    : "No columns selected";
  return (
    <ExpandableSection
      headerText="Settings in this preset"
      expanded={expanded}
      onChange={({ detail }) => setExpanded(detail.expanded)}
    >
      {/* Keyboard users must be able to scroll the bounded preview. */}
      {/* eslint-disable jsx-a11y/no-noninteractive-tabindex */}
      {expanded && (
        <div
          className="oms-preset-changes"
          role="region"
          aria-label="Preset settings to save"
          tabIndex={0}
        >
          <SpaceBetween size="m">
            {scope !== "columns" && (
              <section aria-label="Rows to save">
                <Box variant="h3">Rows to export</Box>
                <Box>
                  {draft.mode === "full" ? "All rows" : "Filtered rows"}
                </Box>
                {draft.mode === "filtered" && (
                  <>
                    <Box color="text-body-secondary">
                      {draft.operation === "or"
                        ? "Any condition (OR)"
                        : "All conditions (AND)"}
                    </Box>
                    <ul
                      className="oms-preset-preview__filters"
                      aria-label="Filters to save"
                    >
                      {draft.criteria
                        .filter((criterion) => criterion.column)
                        .map((criterion) => (
                          <li
                            key={criterion.id}
                            className="oms-preset-preview__item"
                          >
                            <Box fontWeight="bold">{criterion.column}</Box>
                            <PresetChangeCell
                              value={{
                                text: "",
                                filter: submittedCondition(criterion),
                              }}
                              report={report}
                            />
                          </li>
                        ))}
                    </ul>
                  </>
                )}
              </section>
            )}
            {scope !== "filters" && (
              <section
                className={
                  scope !== "columns" ? "oms-preset-preview__item" : ""
                }
                aria-label="Columns to save"
              >
                <Box variant="h3">Output columns</Box>
                <PresetChangeCell
                  value={{
                    text: allColumns
                      ? "All columns (report order)"
                      : selectedLabel,
                    values: names,
                    kind: "columns",
                  }}
                  report={report}
                />
              </section>
            )}
          </SpaceBetween>
        </div>
      )}
      {/* eslint-enable jsx-a11y/no-noninteractive-tabindex */}
    </ExpandableSection>
  );
}

export default function OMSPresetChanges({
  saved,
  draft,
  name,
  scope,
  report,
}: {
  saved: OmsExportPreset;
  draft: OmsConfiguration;
  name: string;
  scope: OmsPresetScope;
  report: OmsReport;
}) {
  const changes = presetChanges(saved, draft, name, scope);
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  return (
    <SpaceBetween size="s">
      <Box variant="h3">Changes to “{saved.name}”</Box>
      {changes.summaries.length ? (
        <>
          <ul className="oms-report-download__applied-filters">
            {changes.summaries.map((summary) => (
              <li key={summary}>{summary}</li>
            ))}
          </ul>
          <ExpandableSection
            headerText={`Change details (${changes.details.length})`}
            expanded={detailsExpanded}
            onChange={({ detail }) => setDetailsExpanded(detail.expanded)}
          >
            {/* Keyboard users must be able to scroll the bounded detail region. */}
            {/* eslint-disable jsx-a11y/no-noninteractive-tabindex */}
            {detailsExpanded && (
              <div
                className="oms-preset-changes"
                role="region"
                aria-label="Saved and updated preset settings"
                tabIndex={0}
              >
                <SpaceBetween size="s">
                  {changes.details.map((change) => (
                    <section
                      key={change.id}
                      className="oms-preset-preview__item"
                      aria-label={change.label}
                    >
                      <Box fontWeight="bold">{change.label}</Box>
                      <div className="oms-preset-preview__comparison">
                        <div>
                          <Box color="text-body-secondary" fontSize="body-s">
                            Saved
                          </Box>
                          <PresetChangeCell
                            value={change.before}
                            report={{
                              ...report,
                              columnTypes: saved.schemaTypes,
                            }}
                          />
                        </div>
                        <div>
                          <Box color="text-body-secondary" fontSize="body-s">
                            Updated
                          </Box>
                          <PresetChangeCell
                            value={change.after}
                            report={report}
                          />
                        </div>
                      </div>
                    </section>
                  ))}
                </SpaceBetween>
              </div>
            )}
            {/* eslint-enable jsx-a11y/no-noninteractive-tabindex */}
          </ExpandableSection>
        </>
      ) : (
        <Box color="text-body-secondary">No settings changed.</Box>
      )}
    </SpaceBetween>
  );
}
